import { get } from "lodash";

import { ValueFormatterParams, ValueGetterParams } from "ag-grid-community";

import { getIsSaleLotVendorBred } from "lib/saleLot/vendorBred";

/**
 * Normalise a drafting attributes age range to months.
 *   { minAge: 6, maxAge: 12, ageRangeTimeUnit: "months" } → { min: 6, max: 12 }
 *   { minAge: 2, maxAge: 4,  ageRangeTimeUnit: "years"  } → { min: 24, max: 48 }
 */
const getAgeMonths = (
  draftingAttributes: DraftingAttributes | null | undefined,
): { min: number | null; max: number | null } => {
  const { minAge, maxAge, ageRangeTimeUnit } = draftingAttributes || {};
  if (minAge == null && maxAge == null) {
    return { min: null, max: null };
  }
  const multiplier = ageRangeTimeUnit === "years" ? 12 : 1;
  return {
    min: minAge != null ? minAge * multiplier : null,
    max: maxAge != null ? maxAge * multiplier : null,
  };
};

/** Return the lower bound of the age range in months (or the upper bound if only max is set), for numeric sorting. */
export const ageMonthsGetter = ({ data }: ValueGetterParams): number | null => {
  if (!data) {
    return null;
  }
  const { min, max } = getAgeMonths(data.draftingAttributes);
  return min ?? max;
};

/** Display the age range in months as "min - max", or a single value when min and max match. */
export const ageMonthsFormatter = ({ data }: ValueFormatterParams): string => {
  if (!data) {
    return "";
  }
  const { min, max } = getAgeMonths(data.draftingAttributes);
  if (min == null && max == null) {
    return "";
  }
  if (min != null && max != null && min !== max) {
    return `${min} - ${max}`;
  }
  return String(min ?? max);
};

/** Resolve a breed ID field to its display name via the breedByIdLookup context. */
export const breedNameGetter = ({
  colDef,
  data,
  context,
}: ValueGetterParams): string | null => {
  if (!data) {
    return null;
  }
  const { breedByIdLookup } = context;
  const breedId = get(data, colDef.field) || null;
  return breedByIdLookup?.[breedId]?.name || null;
};

/** Return "Estimate" or "Actual" based on a boolean weight-estimation flag. */
export const estimatedWeightGetter = ({
  data,
  colDef,
}: ValueGetterParams): string | null => {
  if (!data) {
    return null;
  }
  const value = get(data, colDef.field);
  if (value == null) {
    return null;
  }
  return value ? "Estimate" : "Actual";
};

export const vendorBredGetter = ({
  data,
}: ValueGetterParams): boolean | null => {
  if (!data) {
    return null;
  }
  return getIsSaleLotVendorBred(data, data.consignment);
};
